import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  getAssignedPlan,
  getCheckoutPlanForRole,
  getDerivedPaymentStatus,
} from "../constants/plans";
import { api, extractApiErrorMessage } from "../services/apiClient";

const buildRegisterPath = (role, planId) => {
  const params = new URLSearchParams();
  if (role) params.set("role", role);
  if (planId) params.set("plan", planId);
  const query = params.toString();
  return query ? `/register?${query}` : "/register";
};

export const useSubscriptionActivation = ({ user, navigate }) => {
  const [activatingPlanId, setActivatingPlanId] = useState(null);

  const activateSubscription = useCallback(
    async ({ planId, role } = {}) => {
      const targetRole = role || user?.role || "basic";
      const targetPlan = planId || getCheckoutPlanForRole(targetRole);

      if (!user) {
        navigate(buildRegisterPath(targetRole, targetPlan));
        return;
      }

      if (user.role === "admin") {
        toast.info(
          "Tu cuenta administradora no puede cambiar de plan desde esta vista.",
        );
        return;
      }

      const expectedPlan = getCheckoutPlanForRole(user.role);

      if (expectedPlan === "free") {
        if (targetPlan === "free") {
          toast.info("Tu cuenta gratuita ya está activa");
          return;
        }

        toast.info(
          "Tu tipo de cuenta actual es Básico. Para iniciar ese plan, usa una cuenta con el rol correspondiente o contacta soporte para cambiar tu tipo de cuenta.",
        );
        return;
      }

      if (targetPlan !== expectedPlan) {
        toast.info(
          "Tu tipo de cuenta ya define el plan disponible. Si necesitas otro plan, cambia de rol.",
        );
        return;
      }

      if (
        getAssignedPlan(user) === expectedPlan &&
        ["active", "trialing"].includes(getDerivedPaymentStatus(user))
      ) {
        toast.info("Ya tienes este plan activo");
        return;
      }

      setActivatingPlanId(expectedPlan);
      try {
        const response = await api.post("/subscription/checkout", {
          plan_id: expectedPlan,
          origin_url: window.location.origin,
        });
        const responseText = await response.text();
        let data = null;
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch {
          data = null;
        }

        if (!response.ok) {
          throw new Error(
            extractApiErrorMessage(data, "No se pudo iniciar la activación"),
          );
        }

        if (!data?.checkout_url) {
          throw new Error("No se pudo crear sesión de pago");
        }

        window.location.href = data.checkout_url;
      } catch (error) {
        toast.error(error.message || "Error al activar suscripción");
      } finally {
        setActivatingPlanId(null);
      }
    },
    [navigate, user],
  );

  return {
    activateSubscription,
    activatingPlanId,
    isActivatingSubscription: Boolean(activatingPlanId),
  };
};
